import { STATUS_VACANCIES } from 'common/constants'
import type { RootState } from 'common/types'
import { selectMemoizedUsers } from 'features/Home/selectors'
import type { ContactsType } from 'features/MyProfile/types'
import { createSelector } from 'reselect'

const selectProfile = (state: RootState) => state.profile

const getMyProfileSelector = (state: RootState) => state.profile.profile

const getOtherProfileSelector = (state: RootState) => state.contacts.otherProfile

export const getDisplayNameSelector = (state: RootState) => state.profile.profile?.displayName
export const getFirstNameSelector = (state: RootState) => state.profile.profile?.first_name
export const getLastNameSelector = (state: RootState) => state.profile.profile?.last_name

export const getMyName = createSelector(getDisplayNameSelector, getFirstNameSelector, getLastNameSelector,
  (displayName, firstName, lastName) => displayName || `${firstName} ${lastName}`)

export const getMyProfile = createSelector(getMyProfileSelector, (profile) => profile)

export const getMyUid = createSelector(getMyProfileSelector, (profile) => profile?.uid ?? '')
export const getMyUsername = createSelector(getMyProfileSelector, (profile) => profile?.username || '')

export const selectMyVacancies = createSelector(selectProfile, ({ vacancies }) => vacancies)
export const selectMyVacanciesLoading = createSelector(selectProfile, ({ isVacanciesLoading }) => isVacanciesLoading)

export const selectTrustLevels = createSelector(selectProfile, ({ trustUsers }) => {
  if (!trustUsers) return {}
  const trustLevels: { [key: string]: number } = {}
  Object.keys(trustUsers).forEach((key) => { trustLevels[key] = trustUsers[key].level })
  return trustLevels
})

export const selectTrustUsers = createSelector(selectProfile, ({ trustUsers }) => {
  if (!trustUsers) return []
  const profiles = Object.values(trustUsers).map(({ profile }) => profile)
  return profiles.length > 1
    ? profiles.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
    : profiles
})

export const selectMyActiveVacancies = createSelector(selectProfile, ({ vacancies }) => vacancies.filter((vacancy) =>
  vacancy?.status === STATUS_VACANCIES.OPEN))

export const selectMyCloseVacancies = createSelector(selectProfile, ({ vacancies }) => vacancies.filter((vacancy) =>
  vacancy?.status === STATUS_VACANCIES.CLOSED))

export const selectSpecialities = (uid: string) =>
  createSelector(selectProfile, ({ specialities }) => specialities[uid] ?? {})

export const selectActiveSpecialities = (uid: string) =>
  createSelector(selectProfile, ({ specialities }) => {
    const userSpecialities = specialities[uid]?.data ?? []
    return userSpecialities.filter((speciality: any) => {
      return speciality?.status === STATUS_VACANCIES.OPEN
    })
  })

export const selectClosedSpecialities = (uid: string) =>
  createSelector(selectProfile, ({ specialities }) => {
    const userSpecialities = specialities[uid]?.data ?? []
    return userSpecialities.filter((speciality: any) => {
      return speciality?.status === STATUS_VACANCIES.CLOSED
    })
  })

export const getProfile = createSelector(getOtherProfileSelector, getMyProfileSelector, (otherProfile, myProfile) => {
  if (otherProfile) return otherProfile
  return myProfile
})

export const getOtherProfile = createSelector(getOtherProfileSelector, (profile) => profile)

export const selectContactUids = createSelector(getMyProfileSelector, (profile) => profile?.contacts ?? [])

export const selectMyBlockedUids = createSelector(getMyProfileSelector, (profile) => profile?.blackList || [])
export const selectMyBlockedByUids = createSelector(getMyProfileSelector, (profile) => profile?.blockedBy || [])

export const selectMyBlockedUsers = createSelector(selectProfile, ({ blockedUsers }) => {
  if (!blockedUsers) return []
  const profiles = Object.values(blockedUsers)
  return profiles.length > 1
    ? profiles.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
    : profiles
})

export const selectTrustedUids = createSelector(getMyProfileSelector, (profile) => profile?.trust || [])

export const selectMyNetworkContacts = createSelector(
  getMyProfileSelector,
  selectMemoizedUsers,
  selectTrustedUids,
  (profile, memoizedUsers, trustedUids) => {
    return !profile?.contacts ? [] : profile.contacts.reduce<ContactsType[]>((acc, uid) => {
      return [
        ...acc,
        { ...memoizedUsers[uid], isTrusted: trustedUids.includes(memoizedUsers[uid]?.uid) }
      ].sort((firstContact: ContactsType, secondContacts: ContactsType) => {
        if (secondContacts.displayName < firstContact.displayName) return 1
        return -1
      })
    }, [])
  }
)

export const selectTrustedProfiles = createSelector(
  selectTrustUsers,
  (profiles) => {
    const trustedData = profiles.reduce<{ [key: string]: string[] }>((acc, profile) => {
      profile.trust?.forEach((trustId) => {
        if (!acc[trustId]) {
          acc[trustId] = []
        }
        acc[trustId].push(profile.displayName)
      })
      return acc
    }, {})

    return { ...trustedData }
  }
)

export const getIsFullTrustContacts = createSelector(getMyProfileSelector, (profile) =>
  profile?.isFullTrustContacts || false)

export const getUidToBeTrusted = createSelector(selectProfile, ({ uidToBeTrusted }) => uidToBeTrusted)

export const getIsEditTrustContacts = createSelector(getMyProfileSelector, (profile) =>
  profile?.isEditTrustContacts || false)

export const getIsOpenAddUserToGroupModal = createSelector(selectProfile, ({ isOpenAddUserToGroupModal }) =>
  isOpenAddUserToGroupModal || false)

export const getIsOpenGroupInviteOptionsModal = createSelector(selectProfile, ({ isOpenGroupInviteOptionsModal }) =>
  isOpenGroupInviteOptionsModal || false)

export const selectIsLoadingTrustOrUntrust = createSelector(selectProfile, ({ isTrustOrUntrustLoading }) =>
  isTrustOrUntrustLoading || false)

export const selectIsBlockingInProgress = createSelector(selectProfile, ({ isBlockingInProgress }) =>
  isBlockingInProgress || false)

export const selectMyGroups = createSelector(selectProfile, ({ groups }) => groups)
export const selectMyGroupsLoading = createSelector(selectProfile, ({ isGroupsLoading }) => isGroupsLoading)
export const selectGroupToJoin = createSelector(selectProfile, ({ groupToJoin }) => groupToJoin)
export const selectGroup = (groupIdentifier: string) => createSelector(
  selectMyGroups,
  (groups) => {
    return groups.find((group) => group.id === groupIdentifier || group.url === groupIdentifier) || undefined
  }
)
